import { useLayoutEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import {
  Check,
  ClipboardCheck,
  LockKeyhole,
  MessageSquare,
  Users,
  type LucideIcon,
} from "lucide-react";
import { cn } from "../../lib/utils.js";
import { useReducedMotion } from "../../lib/motion.js";
import {
  CAMPAIGN_DRAFT_STEPS,
  campaignDraftConnectorTone,
  campaignDraftStepVisualState,
  canSelectCampaignDraftStep,
  type CampaignDraftConnectorTone,
  type CampaignDraftStepId,
  type CampaignDraftStepVisualState,
} from "./campaign-draft-workflow.js";

const STEP_ICONS: Record<CampaignDraftStepId, LucideIcon> = {
  audience: Users,
  message: MessageSquare,
  review: ClipboardCheck,
};

function connectorClass(tone: CampaignDraftConnectorTone): string {
  switch (tone) {
    case "accent":
      return "bg-accent";
    case "muted":
      return "bg-line";
    default: {
      const _exhaustive: never = tone;
      return _exhaustive;
    }
  }
}

function markerClass(state: CampaignDraftStepVisualState): string {
  switch (state) {
    case "CURRENT":
      return "bg-accent text-accent-contrast";
    case "COMPLETED":
      return "bg-accent/15 text-accent";
    case "AVAILABLE":
      return "border border-line bg-surface text-muted";
    case "LOCKED":
      return "border border-line/80 bg-surface-muted text-subtle";
    default: {
      const _exhaustive: never = state;
      return _exhaustive;
    }
  }
}

function stepLabelClass(state: CampaignDraftStepVisualState): string {
  switch (state) {
    case "CURRENT":
      return "font-semibold text-accent";
    case "COMPLETED":
      return "font-medium text-accent";
    case "AVAILABLE":
      return "font-medium text-foreground/80";
    case "LOCKED":
      return "font-medium text-subtle";
    default: {
      const _exhaustive: never = state;
      return _exhaustive;
    }
  }
}

export function CampaignDraftStepper({
  active,
  messageUnlocked,
  reviewUnlocked,
  reviewNeedsAttention = false,
  onSelectStep,
}: {
  active: CampaignDraftStepId;
  messageUnlocked: boolean;
  reviewUnlocked: boolean;
  reviewNeedsAttention?: boolean;
  onSelectStep?: (step: CampaignDraftStepId) => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const reduceMotion = useReducedMotion() ?? false;
  const scrollerRef = useRef<HTMLOListElement>(null);
  const unlocks = { messageUnlocked, reviewUnlocked };

  useLayoutEffect(() => {
    const scroller = scrollerRef.current;
    if (!scroller) return;
    const current = scroller.querySelector(`[data-testid="campaign-step-${active}"]`);
    if (!(current instanceof HTMLElement)) return;
    if (scroller.scrollWidth <= scroller.clientWidth + 1) return;
    const left = current.offsetLeft - (scroller.clientWidth - current.offsetWidth) / 2;
    scroller.scrollTo({
      left: Math.max(0, left),
      behavior: reduceMotion ? "auto" : "smooth",
    });
  }, [active, reduceMotion]);

  return (
    <div className="min-w-0 w-full overflow-hidden rounded-lg border border-line/60 bg-surface/90 px-2 py-1.5 md:px-3">
      <ol
        ref={scrollerRef}
        className="flex min-w-0 w-full items-center overflow-x-auto overscroll-x-contain scroll-ps-2 scroll-pe-2 md:overflow-x-visible"
        data-testid="campaign-draft-stepper"
      >
        {CAMPAIGN_DRAFT_STEPS.map((step, index) => {
          const state = campaignDraftStepVisualState(step, { active, ...unlocks });
          const selectable = canSelectCampaignDraftStep(step, unlocks) && onSelectStep != null;
          const isLast = index === CAMPAIGN_DRAFT_STEPS.length - 1;
          const tone = campaignDraftConnectorTone(step, { active, ...unlocks });
          const StepIcon = STEP_ICONS[step];

          const marker = (
            <span
              className={cn(
                "inline-grid size-6 shrink-0 place-items-center rounded-full text-[0.65rem] font-semibold tabular-nums",
                markerClass(state),
              )}
              aria-hidden="true"
            >
              {state === "COMPLETED" ? (
                <Check size={12} strokeWidth={2.5} />
              ) : (
                index + 1
              )}
            </span>
          );

          const label = (
            <>
              {marker}
              <StepIcon size={13} className="shrink-0" aria-hidden="true" />
              <span className="whitespace-nowrap">{t(`campaigns.steps.${step}`)}</span>
              {state === "LOCKED" ? (
                <>
                  <LockKeyhole size={11} className="shrink-0 opacity-70" aria-hidden="true" />
                  <span className="sr-only">{t("campaigns.steps.locked")}</span>
                </>
              ) : step === "review" && reviewNeedsAttention ? (
                <>
                  <span
                    className="size-1.5 shrink-0 rounded-full bg-warning"
                    aria-hidden="true"
                    data-testid="campaign-step-review-attention"
                  />
                  <span className="sr-only">{t("campaigns.review.attentionStep")}</span>
                </>
              ) : null}
            </>
          );

          const className = cn(
            "inline-flex items-center gap-1 rounded-md px-0.5 py-0.5 text-[0.75rem] md:gap-1.5 md:px-1",
            stepLabelClass(state),
            selectable && state !== "CURRENT" ? "hover:bg-surface-muted cursor-pointer" : null,
          );

          return (
            <li
              key={step}
              className={cn(
                "flex items-center",
                isLast ? "shrink-0" : "shrink-0 md:min-w-0 md:flex-1",
              )}
            >
              {selectable ? (
                <button
                  type="button"
                  className={className}
                  aria-current={state === "CURRENT" ? "step" : undefined}
                  onClick={() => onSelectStep?.(step)}
                  data-testid={`campaign-step-${step}`}
                  data-state={state}
                >
                  {label}
                </button>
              ) : (
                <span
                  className={className}
                  aria-current={state === "CURRENT" ? "step" : undefined}
                  aria-disabled={state === "LOCKED" || undefined}
                  data-testid={`campaign-step-${step}`}
                  data-state={state}
                  title={
                    state === "LOCKED" && step === "message"
                      ? t("campaigns.message.stepLocked")
                      : undefined
                  }
                >
                  {label}
                </span>
              )}
              {isLast ? null : (
                <span
                  className={cn(
                    "mx-1.5 h-0.5 w-5 shrink-0 md:mx-3 md:min-w-8 md:w-auto md:flex-1",
                    connectorClass(tone),
                  )}
                  aria-hidden="true"
                  data-testid={`campaign-step-connector-${step}`}
                  data-filled={tone === "muted" ? "false" : "true"}
                  data-tone={tone}
                />
              )}
            </li>
          );
        })}
      </ol>
    </div>
  );
}
